import React from "react";
import {Avatar, Box, Button, Container, Typography} from "@mui/material";
import MailIcon from "@mui/icons-material/MailOutline";
import DownloadIcon from "@mui/icons-material/FileDownloadOutlined";
import {Link} from "react-router-dom";
import PageHeader from "../components/modern/PageHeader";
import SectionTitle from "../components/modern/SectionTitle";
import Reveal from "../components/modern/Reveal";
import history from "../store/history";
import diaporamas from "../store/diaporama";
import {CONTACT_MAIL} from "../config";
import {colors, displayFont} from "../styling/GlobalTheme";

const img = (p: string) => p.startsWith("/") || p.startsWith("http") ? p : "/" + p;
// Photos lourdes remplacées par une version allégée pour le web
const light: Record<string, string> = {
    "images/romain/romain3.jpeg": "/images/web/romain3.jpg",
    "images/maxime/maxime6.jpeg": "/images/web/maxime6.jpg",
};

const committee = [
    ["Président", "Samuel Lebsir"],
    ["Vice-président", "Raphaël Gaillard"],
    ["Secrétaire", "Amélie Wenger-Reymond"],
    ["Caissier", "François Théytaz"],
    ["Membre", "Johny Beney"],
];

const stats = [
    ["≈ 100", "membres"],
    ["225+", "victoires en Coupe du monde"],
    ["20+", "titres de champion du monde"],
    ["25", "ans en 2027"],
];

const initials = (n: string) => n.split(/[\s-]+/).map(w => w[0]).slice(0, 2).join("");

function Club() {
    const h = history[0];
    return (
        <>
            <PageHeader title="Le Club" subtitle="Le Mouch'Paba Telemark Club, à Thyon dans le domaine des 4 Vallées."
                        image={process.env.PUBLIC_URL + "/images/mouchtec/mouch_4.jpg"}/>

            {/* Présentation + chiffres */}
            <Box component="section" sx={{py: {xs: 7, md: 10}}}>
                <Container maxWidth="lg" sx={{display: "grid", gridTemplateColumns: {xs: "1fr", md: "1.2fr 1fr"}, gap: {xs: 5, md: 8}, alignItems: "center"}}>
                    <Reveal>
                        <Typography sx={{color: colors.glacierDark, fontWeight: 800, letterSpacing: 2, textTransform: "uppercase", fontSize: ".8rem"}}>Notre histoire</Typography>
                        <Typography variant="h2" sx={{fontSize: {xs: "2.6rem", md: "3.6rem"}, mb: 2}}>Le club le plus titré du télémark</Typography>
                        <Typography sx={{color: "text.secondary", mb: 2, fontSize: "1.05rem"}}>{h.text}</Typography>
                        <Typography sx={{color: "text.secondary", fontSize: "1.05rem"}}>{h.text2}</Typography>
                    </Reveal>
                    <Box sx={{display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2}}>
                        {stats.map(([v, l], i) => (
                            <Reveal key={l} delay={i * 100}>
                                <Box sx={{bgcolor: i % 3 === 0 ? colors.night : "#fff", color: i % 3 === 0 ? "#fff" : colors.night, borderRadius: 4, p: 3, height: "100%",
                                    boxShadow: "0 10px 30px rgba(11,18,32,.08)"}}>
                                    <Typography sx={{fontFamily: displayFont, fontSize: {xs: "2.8rem", md: "3.6rem"}, lineHeight: 1, color: i % 3 === 0 ? colors.glacier : colors.glacierDark}}>{v}</Typography>
                                    <Typography sx={{fontSize: ".9rem", mt: .5, opacity: .85}}>{l}</Typography>
                                </Box>
                            </Reveal>
                        ))}
                    </Box>
                </Container>
            </Box>

            {/* Champions en images */}
            <Box component="section" sx={{py: {xs: 7, md: 10}, bgcolor: colors.night, color: "#fff"}}>
                <Container maxWidth="lg">
                    <SectionTitle light kicker="Nos athlètes" title="Le club en images"
                                  action={<Button component={Link} to="/champions" variant="outlined" sx={{color: "#fff", borderColor: "#fff"}}>Palmarès des coureurs</Button>}/>
                    <Box sx={{display: "grid", gridTemplateColumns: {xs: "1fr 1fr", md: "repeat(4, 1fr)"}, gridAutoRows: {xs: 200, md: 300}, gap: 1.5}}>
                        {diaporamas.map((d, i) => {
                            const [name, credit] = d.label.split(/,\s*picture:/);
                            return (
                                <Reveal key={d.label} delay={(i % 4) * 80}
                                        style={{position: "relative", borderRadius: 16, overflow: "hidden"}}>
                                    <Box sx={{position: "absolute", inset: 0, "&:hover img": {transform: "scale(1.06)"}}}>
                                        <img src={light[d.imgPath.replace(/^\//, "")] || img(d.imgPath)} alt={name} loading="lazy"
                                             style={{width: "100%", height: "100%", objectFit: "cover", transition: "transform .6s"}}/>
                                        <Box sx={{position: "absolute", inset: "auto 0 0", p: 1.5, background: "linear-gradient(transparent, rgba(0,0,0,.8))"}}>
                                            <Typography sx={{fontWeight: 700, fontSize: ".95rem"}}>{name}</Typography>
                                            {credit && <Typography sx={{fontSize: ".7rem", opacity: .7}}>© {credit}</Typography>}
                                        </Box>
                                    </Box>
                                </Reveal>
                            );
                        })}
                    </Box>
                </Container>
            </Box>

            {/* Jeunesse */}
            <Box component="section" sx={{py: {xs: 7, md: 10}}}>
                <Container maxWidth="lg" sx={{display: "grid", gridTemplateColumns: {xs: "1fr", md: "1fr 1fr"}, gap: {xs: 4, md: 8}, alignItems: "center"}}>
                    <Reveal>
                        <Box sx={{borderRadius: 5, overflow: "hidden", aspectRatio: "4 / 3", boxShadow: "0 30px 60px rgba(11,18,32,.18)"}}>
                            <img src={process.env.PUBLIC_URL + "/images/2024mouchtec/mouch6.jpg"} alt="Sortie du club" style={{width: "100%", height: "100%", objectFit: "cover"}}/>
                        </Box>
                    </Reveal>
                    <Reveal delay={150}>
                        <Typography sx={{color: colors.glacierDark, fontWeight: 800, letterSpacing: 2, textTransform: "uppercase", fontSize: ".8rem"}}>Esprit de club</Typography>
                        <Typography variant="h2" sx={{fontSize: {xs: "2.6rem", md: "3.6rem"}, mb: 2}}>Pour tous les âges</Typography>
                        <Typography sx={{color: "text.secondary", mb: 3, fontSize: "1.05rem"}}>{h.text3}</Typography>
                        <Box sx={{display: "flex", gap: 1.5, flexWrap: "wrap"}}>
                            <Button variant="contained" color="secondary" component={Link} to="/mouchtec">Le Mouch'Tec</Button>
                            <Button variant="outlined" color="inherit" component={Link} to="/activites">Le programme de la saison</Button>
                        </Box>
                    </Reveal>
                </Container>
            </Box>

            {/* Comité */}
            <Box component="section" sx={{py: {xs: 7, md: 10}, bgcolor: "#fff"}}>
                <Container maxWidth="lg">
                    <SectionTitle kicker="Qui sommes-nous" title="Le comité"/>
                    <Box sx={{display: "grid", gridTemplateColumns: {xs: "1fr", sm: "1fr 1fr", md: "repeat(5, 1fr)"}, gap: 2}}>
                        {committee.map(([role, name], i) => (
                            <Reveal key={name} delay={i * 80}>
                                <Box sx={{textAlign: "center", p: 3, borderRadius: 4, bgcolor: colors.snow, minHeight: 190}}>
                                    <Avatar sx={{width: 72, height: 72, mx: "auto", mb: 1.5, bgcolor: colors.night, color: colors.glacier, fontFamily: displayFont, fontSize: "1.8rem"}}>
                                        {initials(name)}
                                    </Avatar>
                                    <Typography sx={{fontWeight: 700}}>{name}</Typography>
                                    <Typography sx={{color: colors.glacierDark, fontSize: ".85rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: 1}}>{role}</Typography>
                                </Box>
                            </Reveal>
                        ))}
                    </Box>
                    <Box sx={{display: "flex", gap: 1.5, flexWrap: "wrap", mt: 4}}>
                        <Button variant="contained" href={`mailto:${CONTACT_MAIL}`} startIcon={<MailIcon/>}>Contacter le club</Button>
                        <Button variant="outlined" color="inherit" href={process.env.PUBLIC_URL + "/docs/Club_Statuts.pdf"} target="_blank" startIcon={<DownloadIcon/>}>Statuts du club (PDF)</Button>
                    </Box>
                </Container>
            </Box>
        </>
    );
}

export default Club;
